// The Commission Modeler's admin-editable vocabulary, resolved server-side.
//
// A module of its own rather than a section of commissionModeler.ts, mirroring
// prequalFunnelCopy.ts: one GET, its response type, and its fetcher, so the wire
// contract has a single home and the render layer consumes it without redeclaring
// it. That file is the model for all of this — the same problem (prose that must be
// admin-editable without a deploy, with a compiled-in default beneath it) solved the
// same way.
//
// GET /commission-modeler/copy takes NO brand parameter. Both underlying document
// keys are Phoenix-tier-only server-side, so the payload cannot vary by partner and
// every broker on every partner site gets the same words.
//
// Published copy only: an admin's unpublished draft never appears here.

import { portalApi } from "./client";

/** The form's own labels and helper text — the half the BROWSER draws, and so the
 *  half the tab compiles in as a fallback.
 *
 *  🔴 These are the real Deal card's Broker Fee Authorization vocabulary, verbatim.
 *  A broker who models a deal here and then reads their signed authorization there
 *  must see one vocabulary, not two dialects of it — which is why they are editable
 *  in ONE place rather than retyped per surface. */
export interface CommissionModelerFormCopy {
  /** The three MONTHLY arrangements — a BrokerFeeMonthlyMode each. */
  modeNone: string;
  modePercentOfSellerShare: string;
  modeFlatMonthly: string;
  percentLabel: string;
  percentHelper: string;
  flatLabel: string;
  /** The three CASH-AT-CLOSING arrangements — a BrokerCashAtClosingMode each, and a
   *  separate vocabulary from the monthly one above on purpose: the two percentages
   *  are taken on different bases (the seller's monthly share vs the buyer's down
   *  payment), so a shared label would leave a broker unsure which they picked. */
  cashModeNone: string;
  cashModeFlat: string;
  cashModePercentOfDownPayment: string;
  /** The BROKER's cash at closing. The qualifier is load-bearing — the unrelated
   *  deal-structure field `Deal.CashAtClosing` is the BUYER's contribution and
   *  renders as a bare "Cash at closing" on the deal Overview tab. */
  cashLabel: string;
  cashHelper: string;
  /** Shown only in percent-of-down-payment mode. */
  cashPercentLabel: string;
  cashPercentHelper: string;
  saleLabel: string;
  saleHelper: string;
}

/** The generated report's title and its disclosure paragraphs — what the
 *  broker's CLIENT will read, so the broker can read it first.
 *
 *  The report's money labels, section headings, schedule column headers and
 *  signature-block labels are registered alongside these server-side but are
 *  deliberately not on this wire: the PDF renderer resolves those itself, and no
 *  browser draws one. */
export interface CommissionReportDisclosureCopy {
  title: string;
  purpose: string;
  illustrativeOnly: string;
  phoenixRole: string;
  /** Qualifies every figure in the document, which is why it renders first. */
  indicativeFigures: string;
  /** Figures are GROSS of program fees — without this a broker reads the lifetime
   *  total as take-home. */
  programFees: string;
  /** Replaces the old `monthlyFigures` block. The buyer's payment and the servicing
   *  spread are off the face of every surface now, so this explains the seller's
   *  receipt from the seller's side and deliberately carries no figure of its own. */
  sellerReceiptComposition: string;
  /** 🔴 The written half of the schedule's figure-clash mitigation: the outstanding
   *  principal column is the note's own balance, NOT the note less the seller
   *  principal column beside it. Without it the table looks like it fails to add up. */
  scheduleNote: string;
  cashAtClosing: string;
  programTerms: string;
  noAdvice: string;
  version: string;
  handling: string;
  acknowledgement: string;
}

/** Every field of both halves is always populated — the server resolves
 *  published-override-else-code-default, so "not yet edited" is a value rather than
 *  a missing key. */
export interface CommissionModelerCopy {
  form: CommissionModelerFormCopy;
  report: CommissionReportDisclosureCopy;
}

/**
 * Resolve the modeler's copy.
 *
 * **Returns `null` on ANY failure, and never throws** — the same posture as
 * `fetchPrequalFunnelCopy`, for the same reason. There is nothing to tell the broker
 * and nothing for them to do: the tab compiles the same wording in as its default,
 * so a 403, an outage or a parse failure degrades to today's labels. Surfacing an
 * error would trade a correct field label for a scary one, and blocking the form on
 * a copy fetch would be worse still.
 */
export async function fetchCommissionModelerCopy(): Promise<CommissionModelerCopy | null> {
  try {
    return await portalApi.get<CommissionModelerCopy>("/commission-modeler/copy");
  } catch {
    return null;
  }
}
